/**
 * DepositCapture · iter303ak-fix23 — Accepted · Task 10 Action 30
 * ────────────────────────────────────────────────────────────────
 * Conditional deposit capture that auto-detects "is a deposit required
 * on any accepted quote?" from the upstream PickAcceptedVariants
 * outcome (`deposit_required_total > 0`). Three states:
 *
 *   • No deposit required (deposit_required_total === 0)
 *     → renders a "No deposit needed" acknowledgement + Continue only
 *   • Deposit required, not yet captured
 *     → captures amount, date, method (Bank transfer / Cash / Cheque /
 *       Other), reference (bank ref, cheque number, etc.), optional
 *       note. Amount pre-filled with the required total.
 *   • Deposit already captured (re-entry)
 *     → shows the captured record + [Edit] button
 *
 * Writes to captured_data:
 *   • deposit_captured: bool
 *   • deposit: { amount, date, method, reference, note, stamped_at }
 *
 * PATCHes the parent appraisal on Save so the record survives page
 * refresh + is visible in the Appraisal Detail view.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Wallet, CheckCircle2, AlertCircle } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const todayLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const money = (n) => {
  const v = Number(n) || 0;
  return `$${v.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function DepositCapture({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;

  // Deposit-required total. Batch T follow-up · workflow integration
  // (2026-02) — prefer the parent appraisal's Deposit Engine value
  // (single-quote path), then fall back to PickAcceptedVariants output
  // (multi-variant path), then legacy fields for BC.
  const requiredTotal = Number(
    capturedData?.appraisal?.deposit_engine_amount
    ?? capturedData?.appraisal_record?.deposit_engine_amount
    ?? capturedData?.deposit_required_total
    ?? capturedData?.pick_accepted_variants?.deposit_required_total
    ?? capturedData?.appraisal?.deposit_amount
    ?? 0,
  );
  const depositRequired = requiredTotal > 0;
  // Reason text from the engine — surfaced under the amount so the
  // operator sees *why* the amount was set (Angle G · UX suggestion).
  const depositReason = (
    capturedData?.appraisal?.deposit_engine_reason
    || capturedData?.appraisal_record?.deposit_engine_reason
    || ''
  );
  // Operator-only: lines with no buy cost make the buy-cost deposit too low.
  const depositWarning = (
    capturedData?.appraisal?.deposit_engine_warning
    || capturedData?.appraisal_record?.deposit_engine_warning
    || ''
  );

  const methodOptions = (params.method_options
    || 'Bank transfer,Cash,Cheque,Card,Other').split(',').map((s) => s.trim()).filter(Boolean);

  const existing = value?.deposit || capturedData?.appraisal?.deposit || {};

  const [amount, setAmount] = useState(existing.amount != null ? String(existing.amount) : String(requiredTotal || ''));
  const [date, setDate] = useState(existing.date || todayLocal());
  const [method, setMethod] = useState(existing.method || methodOptions[0] || 'Bank transfer');
  const [reference, setReference] = useState(existing.reference || '');
  const [note, setNote] = useState(existing.note || '');
  const [captured, setCaptured] = useState(!!existing.stamped_at);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Persist to captured_data whenever anything meaningful changes.
  const derivedState = useMemo(() => ({
    deposit_captured: captured || !depositRequired,
    deposit: depositRequired ? {
      amount: Number(amount) || 0,
      date,
      method,
      reference,
      note,
    } : null,
  }), [captured, depositRequired, amount, date, method, reference, note]);

  useEffect(() => {
    if (onChange) onChange(derivedState);
  }, [derivedState, onChange]);

  const confirm = async () => {
    // Not-required path — nothing to capture, just advance.
    if (!depositRequired) {
      if (appraisalId) {
        try { await api.patch(`/appraisals/${appraisalId}`, { deposit_required: false, deposit_captured_at: null }); }
        catch { /* non-blocking */ }
      }
      onNext();
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setErr('Enter the deposit amount received (must be > 0).');
      return;
    }
    setSaving(true); setErr('');
    try {
      const nowIso = new Date().toISOString();
      const payload = {
        deposit_required: true,
        deposit_captured_at: nowIso,
        deposit: {
          amount: Number(amount),
          date,
          method,
          reference,
          note,
          stamped_at: nowIso,
        },
      };
      if (appraisalId) {
        await api.patch(`/appraisals/${appraisalId}`, payload);
      }
      setCaptured(true);
      onChange && onChange({ ...derivedState, deposit_captured: true, deposit: payload.deposit });
      onNext();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  // ── UI branches ────────────────────────────────────────────────────
  if (!depositRequired) {
    return (
      <div className="space-y-4" data-testid="wf-deposit-not-required">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-start gap-3">
            <Wallet className="w-5 h-5 text-slate-500 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-800">No deposit required</div>
              <div className="text-sm text-slate-600 mt-1">
                None of the accepted quote(s) have a deposit amount on file — nothing to capture here. Continue when ready.
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 pt-2">
          {canBack ? (
            <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-deposit-back">← Back</button>
          ) : <span />}
          <div className="flex gap-2">
            {onCancel && (
              <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-deposit-cancel">Cancel</button>
            )}
            <button
              onClick={confirm}
              className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-2"
              data-testid="wf-deposit-skip"
            >
              <CheckCircle2 className="w-4 h-4" /> Continue (no deposit)
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="wf-deposit-capture">
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
        <div className="flex items-start gap-3">
          <Wallet className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">Deposit — {money(requiredTotal)} required</div>
            <div className="text-sm text-amber-800 mt-1">
              {params.prompt || `Capture the deposit received from the client. Required deposit total (from accepted quote deposit_amount fields): ${money(requiredTotal)}.`}
            </div>
            {/* Batch D · UX polish (Feb 2026): surface the Deposit
                Engine's reason line so Trevor sees WHY the amount
                is what it is (materials-heavy vs standard vs
                small-job cutoff). Data hydrated in Batch A via
                `capturedData.appraisal.deposit_engine_reason`. */}
            {depositReason && (
              <div
                className="text-xs text-amber-900/80 mt-2 pl-2 border-l-2 border-amber-300 italic"
                data-testid="wf-deposit-engine-reason"
              >
                {depositReason}
              </div>
            )}
            {depositWarning && (
              <div className="text-xs font-semibold text-red-700 mt-1 pl-2 border-l-2 border-red-400" data-testid="wf-deposit-engine-warning">
                {depositWarning}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 border border-slate-200 rounded-lg bg-white space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Amount received</label>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full pl-6 pr-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                data-testid="wf-deposit-amount"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Date received</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="wf-deposit-date"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Method</label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="wf-deposit-method"
            >
              {methodOptions.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Reference (bank ref / cheque no)</label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. 20260228-RTR-1234"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="wf-deposit-reference"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Note (optional)</label>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Any context about the payment"
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            data-testid="wf-deposit-note"
          />
        </div>
        {Number(amount) > 0 && Number(amount) < requiredTotal && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            ⚠ Partial deposit — {money(Number(amount))} of {money(requiredTotal)} required. Balance outstanding: <b>{money(requiredTotal - Number(amount))}</b>
          </div>
        )}
      </div>

      {err && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3">
          <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-deposit-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && (
            <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-deposit-cancel">Cancel</button>
          )}
          <button
            onClick={confirm}
            disabled={saving}
            className="px-4 py-1.5 rounded-md text-sm bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-deposit-confirm"
          >
            <CheckCircle2 className="w-4 h-4" /> {saving ? 'Saving…' : 'Save deposit & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
